import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import i18n from '../i18n/index.js';

export const useSettingsStore = defineStore('settings', () => {
  const getDefaultUnitsByLocale = () => {
    const locale = navigator.language; // e.g., "en-US", "en-GB", "de-DE"
    let defaultTemperatureUnits = 'celsius';
    let defaultWindSpeedUnits = 'km/h';
    let defaultPrecipitationUnits = 'mm';

    // Imperial units for US and Liberia
    if (locale.includes('en-US') || locale.includes('en-LR')) {
      defaultTemperatureUnits = 'fahrenheit';
      defaultWindSpeedUnits = 'mph';
      defaultPrecipitationUnits = 'inches';
    }
    // UK uses mph for wind, but Celsius and mm for temp/precip
    else if (locale.includes('en-GB')) {
      defaultWindSpeedUnits = 'mph';
    }

    return {
      temperature: defaultTemperatureUnits,
      windSpeed: defaultWindSpeedUnits,
      precipitation: defaultPrecipitationUnits,
    };
  };

  const defaultUnits = getDefaultUnitsByLocale();

  const temperatureUnits = ref(localStorage.getItem('temperatureUnits') || defaultUnits.temperature);
  const windSpeedUnits = ref(localStorage.getItem('windSpeedUnits') || defaultUnits.windSpeed);
  const precipitationUnits = ref(localStorage.getItem('precipitationUnits') || defaultUnits.precipitation);
  const hideEarlyMorning = ref((localStorage.getItem('hideEarlyMorning') ?? 'true') === 'true');
  const theme = ref(localStorage.getItem('theme') || 'system');
  const showMinMax = ref((localStorage.getItem('showMinMax') ?? 'true') === 'true');
  const provider = ref(localStorage.getItem('provider') || 'auto');
  const locale = i18n.global.locale;

  const stateRefs = {
    temperatureUnits,
    windSpeedUnits,
    precipitationUnits,
    hideEarlyMorning,
    theme,
    showMinMax,
    provider,
    locale,
  };

  function applyTheme(currentTheme) {
    const htmlElement = document.documentElement;
    if (currentTheme === 'dark' || (currentTheme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      htmlElement.classList.add('dark');
    } else {
      htmlElement.classList.remove('dark');
    }
  }

  function setSetting(key, value) {
    if (Object.prototype.hasOwnProperty.call(stateRefs, key)) {
      stateRefs[key].value = value;
      localStorage.setItem(key, String(value));
    }
  }

  watch(theme, applyTheme, { immediate: true, flush: 'sync' });

  return {
    temperatureUnits,
    windSpeedUnits,
    precipitationUnits,
    hideEarlyMorning,
    theme,
    showMinMax,
    provider,
    locale,
    setSetting,
  };
});